import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, can } from "../lib/api";
import { formatQAR } from "../lib/money";
const colFilterInput = {
    width: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    border: "1px solid var(--line)",
    borderRadius: 6,
    padding: "5px 7px",
    fontSize: "0.76rem",
    fontFamily: "inherit",
    background: "var(--surface)",
};
const GRID_COLUMNS = "0.35fr 1.1fr 1.2fr 0.9fr 1fr 1fr 1.1fr 0.8fr 1fr";
function SortableHeader({ label, sortKey, sort, onSort, align, }) {
    const active = sort?.key === sortKey;
    return (<button onClick={() => onSort(sortKey)} title="Sort" style={{
            display: "flex",
            alignItems: "center",
            justifyContent: align === "right" ? "flex-end" : "flex-start",
            gap: 4,
            border: "none",
            background: "none",
            padding: 0,
            cursor: "pointer",
            fontFamily: "inherit",
            fontSize: "0.66rem",
            color: active ? "var(--ink)" : "var(--ink-faint)",
            textTransform: "uppercase",
        }}>
      {label}
      <span style={{ fontSize: "0.6rem", opacity: active ? 1 : 0.35 }}>{active ? (sort.dir === "asc" ? "▲" : "▼") : "▲▼"}</span>
    </button>);
}
const AGEING_BUCKET_LABELS = {
    "0-30": "0–30 Days",
    "31-60": "31–60 Days",
    "61-90": "61–90 Days",
    "91-180": "91–180 Days",
    "181-365": "181–365 Days",
    "365+": "Above 365 Days",
};
export function CaseList() {
    const [cases, setCases] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [legalFirms, setLegalFirms] = useState([]);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState("");
    const [searchParams, setSearchParams] = useSearchParams();
    // Per-column filters, applied client-side on top of the loaded page
    // (same as the free-text search above) so they combine with each other
    // and with the URL-driven filters (status/company/ageing) without a
    // round-trip per keystroke.
    const [colFilters, setColFilters] = useState({
        caseCode: "",
        customer: "",
        dateFrom: "",
        dateTo: "",
        type: "",
        stage: "",
        legalFirmId: "",
        priority: "",
    });
    const setCol = (key, value) => setColFilters((f) => ({ ...f, [key]: value }));
    const hasColFilters = Object.values(colFilters).some(Boolean);
    function clearColFilters() {
        setColFilters({ caseCode: "", customer: "", dateFrom: "", dateTo: "", type: "", stage: "", legalFirmId: "", priority: "" });
    }
    const [sort, setSort] = useState(null);
    // Narrow layout only (legal.css swaps the table for cards): the column
    // filters live in the table header, so cards get them in a fold-out panel.
    const [cardFiltersOpen, setCardFiltersOpen] = useState(false);
    function toggleSort(key) {
        setSort((s) => (s?.key === key ? (s.dir === "asc" ? { key, dir: "desc" } : null) : { key, dir: "asc" }));
    }
    const PRIORITY_RANK = { LOW: 0, MEDIUM: 1, HIGH: 2, CRITICAL: 3 };
    const status = searchParams.get("status");
    const overdue = searchParams.get("overdue");
    const companyId = searchParams.get("companyId");
    const ageingBucket = searchParams.get("ageingBucket");
    useEffect(() => {
        api.get("/companies").then((r) => setCompanies(r.items));
        api.get("/customers").then((r) => setCustomers(r.items));
        api.get("/legal-firms").then((r) => setLegalFirms(r.items));
    }, []);
    const customerName = (id) => customers.find((c) => c.id === id)?.name ?? "—";
    const legalFirmName = (id) => (id ? legalFirms.find((f) => f.id === id)?.name ?? "—" : "—");
    const visibleCases = cases.filter((c) => {
        if (search.trim()) {
            const q = search.trim().toLowerCase();
            const matches = c.caseCode.toLowerCase().includes(q) ||
                customerName(c.customerId).toLowerCase().includes(q) ||
                (c.caseNumber ?? "").toLowerCase().includes(q) ||
                c.caseType.toLowerCase().includes(q);
            if (!matches)
                return false;
        }
        if (colFilters.caseCode && !c.caseCode.toLowerCase().includes(colFilters.caseCode.toLowerCase()))
            return false;
        if (colFilters.customer && !customerName(c.customerId).toLowerCase().includes(colFilters.customer.toLowerCase()))
            return false;
        if (colFilters.dateFrom && (!c.caseDate || c.caseDate.slice(0, 10) < colFilters.dateFrom))
            return false;
        if (colFilters.dateTo && (!c.caseDate || c.caseDate.slice(0, 10) > colFilters.dateTo))
            return false;
        if (colFilters.type && c.caseType !== colFilters.type)
            return false;
        if (colFilters.stage && c.legalStage !== colFilters.stage)
            return false;
        if (colFilters.legalFirmId && (c.legalFirmId ?? "") !== colFilters.legalFirmId)
            return false;
        if (colFilters.priority && c.priority !== colFilters.priority)
            return false;
        return true;
    });
    const distinct = (values) => [...new Set(values.filter(Boolean))].sort();
    const typeOptions = distinct(cases.map((c) => c.caseType));
    const stageOptions = distinct(cases.map((c) => c.legalStage));
    const priorityOptions = distinct(cases.map((c) => c.priority));
    const legalFirmOptions = legalFirms.filter((f) => cases.some((c) => c.legalFirmId === f.id)).sort((a, b) => a.name.localeCompare(b.name));
    const sortedCases = sort
        ? [...visibleCases].sort((a, b) => {
            let cmp = 0;
            switch (sort.key) {
                case "caseCode":
                    cmp = a.caseCode.localeCompare(b.caseCode);
                    break;
                case "customer":
                    cmp = customerName(a.customerId).localeCompare(customerName(b.customerId));
                    break;
                case "caseDate":
                    cmp = (a.caseDate ?? "").localeCompare(b.caseDate ?? "");
                    break;
                case "caseType":
                    cmp = a.caseType.localeCompare(b.caseType);
                    break;
                case "legalStage":
                    cmp = a.legalStage.localeCompare(b.legalStage);
                    break;
                case "legalFirm":
                    cmp = legalFirmName(a.legalFirmId).localeCompare(legalFirmName(b.legalFirmId));
                    break;
                case "priority":
                    cmp = (PRIORITY_RANK[a.priority] ?? -1) - (PRIORITY_RANK[b.priority] ?? -1);
                    break;
                case "outstanding":
                    cmp = a.financialSummary.outstandingBalance - b.financialSummary.outstandingBalance;
                    break;
            }
            return sort.dir === "asc" ? cmp : -cmp;
        })
        : visibleCases;
    useEffect(() => {
        const params = new URLSearchParams();
        if (status)
            params.set("caseStatus", status);
        if (overdue)
            params.set("overdue", overdue);
        if (companyId)
            params.set("companyId", companyId);
        if (ageingBucket)
            params.set("ageingBucket", ageingBucket);
        api
            .get(`/cases?${params.toString()}`)
            .then((r) => setCases(r.items))
            .catch(() => setError("Couldn't load the cases."));
    }, [status, overdue, companyId, ageingBucket]);
    const filterParts = [];
    if (companyId)
        filterParts.push(companies.find((c) => c.id === companyId)?.name ?? "Selected company");
    if (status) {
        if (status === "OPEN")
            filterParts.push("Open cases");
        else if (status === "OPEN_EXCL_COURT")
            filterParts.push("Open cases (excluding Court Proceedings)");
        else if (status === "CLOSED_ALL")
            filterParts.push("Closed cases");
        else
            filterParts.push(`Status: ${status.replace(/_/g, " ")}`);
    }
    if (overdue)
        filterParts.push("Cases with an overdue follow-up");
    if (ageingBucket)
        filterParts.push(`Age: ${AGEING_BUCKET_LABELS[ageingBucket] ?? ageingBucket}`);
    const activeFilterLabel = filterParts.length ? filterParts.join(" · ") : null;
    return (<div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
        <h1 style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: "1.6rem", margin: 0 }}>Cases</h1>
        {can("create") && (<Link to="/legal-cases/cases/new" style={{ background: "var(--brand)", color: "#fff", fontWeight: 600, fontSize: "0.82rem", padding: "9px 16px", borderRadius: 8, textDecoration: "none" }}>
          + New Case
        </Link>)}
      </div>

      {(activeFilterLabel || hasColFilters) && (<div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, fontSize: "0.82rem", flexWrap: "wrap" }}>
          {activeFilterLabel && (<span className="mono" style={{ background: "var(--surface-2)", border: "1px solid var(--line-soft)", borderRadius: 6, padding: "4px 10px" }}>
              {activeFilterLabel}
            </span>)}
          {activeFilterLabel && (<button onClick={() => setSearchParams({})} style={{ border: "none", background: "none", color: "var(--accent-ink)", cursor: "pointer", fontSize: "0.8rem", padding: 0 }}>
              Clear filter
            </button>)}
          {hasColFilters && (<button onClick={clearColFilters} style={{ border: "none", background: "none", color: "var(--accent-ink)", cursor: "pointer", fontSize: "0.8rem", padding: 0 }}>
              Clear column filters
            </button>)}
        </div>)}

      <input placeholder="Search by case code, customer, case number, or type…" value={search} onChange={(e) => setSearch(e.target.value)} style={{
            width: "100%",
            maxWidth: 420,
            border: "1px solid var(--line)",
            borderRadius: 8,
            padding: "9px 12px",
            fontSize: "0.85rem",
            fontFamily: "inherit",
            marginBottom: 14,
        }}/>

      {error && <div style={{ color: "var(--st-risk)" }}>{error}</div>}

      <div className="lg-table lg-table-wide lg-case-table" style={{ background: "var(--surface)", border: "1px solid var(--line-soft)", borderRadius: 10, overflow: "hidden" }}>
        <div className="mono" style={{
            display: "grid",
            gridTemplateColumns: GRID_COLUMNS,
            gap: 8,
            padding: "9px 18px",
            background: "var(--surface-2)",
        }}>
          <div style={{ fontSize: "0.66rem", color: "var(--ink-faint)", textTransform: "uppercase" }}>#</div>
          <SortableHeader label="Case" sortKey="caseCode" sort={sort} onSort={toggleSort}/>
          <SortableHeader label="Customer" sortKey="customer" sort={sort} onSort={toggleSort}/>
          <SortableHeader label="Case Date" sortKey="caseDate" sort={sort} onSort={toggleSort}/>
          <SortableHeader label="Type" sortKey="caseType" sort={sort} onSort={toggleSort}/>
          <SortableHeader label="Stage" sortKey="legalStage" sort={sort} onSort={toggleSort}/>
          <SortableHeader label="Legal Firm" sortKey="legalFirm" sort={sort} onSort={toggleSort}/>
          <SortableHeader label="Priority" sortKey="priority" sort={sort} onSort={toggleSort}/>
          <SortableHeader label="Outstanding" sortKey="outstanding" sort={sort} onSort={toggleSort} align="right"/>
        </div>
        <div style={{
            display: "grid",
            gridTemplateColumns: GRID_COLUMNS,
            gap: 8,
            padding: "8px 18px",
            background: "var(--surface-2)",
            borderTop: "1px solid var(--line-soft)",
        }}>
          <div />
          <input placeholder="Filter…" value={colFilters.caseCode} onChange={(e) => setCol("caseCode", e.target.value)} style={colFilterInput}/>
          <input placeholder="Filter…" value={colFilters.customer} onChange={(e) => setCol("customer", e.target.value)} style={colFilterInput}/>
          <div style={{ display: "flex", gap: 3 }}>
            <input type="date" value={colFilters.dateFrom} onChange={(e) => setCol("dateFrom", e.target.value)} title="From" style={{ ...colFilterInput, minWidth: 0, padding: "5px 4px", fontSize: "0.7rem" }}/>
            <input type="date" value={colFilters.dateTo} onChange={(e) => setCol("dateTo", e.target.value)} title="To" style={{ ...colFilterInput, minWidth: 0, padding: "5px 4px", fontSize: "0.7rem" }}/>
          </div>
          <select value={colFilters.type} onChange={(e) => setCol("type", e.target.value)} style={colFilterInput}>
            <option value="">All</option>
            {typeOptions.map((t) => (<option key={t} value={t}>
                {t}
              </option>))}
          </select>
          <select value={colFilters.stage} onChange={(e) => setCol("stage", e.target.value)} style={colFilterInput}>
            <option value="">All</option>
            {stageOptions.map((s) => (<option key={s} value={s}>
                {s.replace(/_/g, " ")}
              </option>))}
          </select>
          <select value={colFilters.legalFirmId} onChange={(e) => setCol("legalFirmId", e.target.value)} style={colFilterInput}>
            <option value="">All</option>
            {legalFirmOptions.map((f) => (<option key={f.id} value={f.id}>
                {f.name}
              </option>))}
          </select>
          <select value={colFilters.priority} onChange={(e) => setCol("priority", e.target.value)} style={colFilterInput}>
            <option value="">All</option>
            {priorityOptions.map((p) => (<option key={p} value={p}>
                {p}
              </option>))}
          </select>
          <div />
        </div>
        {sortedCases.map((c, i) => (<Link key={c.id} to={`/legal-cases/cases/${c.id}`} style={{
                display: "grid",
                gridTemplateColumns: GRID_COLUMNS,
                gap: 8,
                padding: "11px 18px",
                borderTop: "1px solid var(--line-soft)",
                textDecoration: "none",
                color: "inherit",
                alignItems: "center",
            }}>
            <div className="mono" style={{ fontSize: "0.78rem", color: "var(--ink-faint)" }}>
              {i + 1}
            </div>
            <div className="mono" style={{ fontSize: "0.8rem", color: "var(--accent-ink)", fontWeight: 600 }}>
              {c.caseCode}
            </div>
            <div style={{ fontSize: "0.83rem" }}>{customerName(c.customerId)}</div>
            <div className="mono" style={{ fontSize: "0.78rem", color: "var(--ink-soft)" }}>
              {c.caseDate ? new Date(c.caseDate).toLocaleDateString() : "—"}
            </div>
            <div style={{ fontSize: "0.83rem" }}>{c.caseType}</div>
            <div style={{ fontSize: "0.78rem", color: "var(--ink-soft)" }}>{c.legalStage.replace(/_/g, " ")}</div>
            <div style={{ fontSize: "0.78rem", color: "var(--ink-soft)" }}>{legalFirmName(c.legalFirmId)}</div>
            <div style={{ fontSize: "0.78rem" }}>{c.priority}</div>
            <div className="mono tabnum" style={{ fontSize: "0.82rem", textAlign: "right" }}>
              {formatQAR(c.financialSummary.outstandingBalance)}
            </div>
          </Link>))}
        {sortedCases.length === 0 && !error && (<div style={{ padding: 24, textAlign: "center", color: "var(--ink-faint)", fontSize: "0.85rem" }}>
            {search.trim() || hasColFilters ? "No cases match your filters." : "No cases found."}
          </div>)}
      </div>

      {/* Narrow layout: one card per case with only the identifying facts, so
          nothing needs a sideways scroll; the rest is on the case page. */}
      <div className="lg-case-cards">
        <button type="button" onClick={() => setCardFiltersOpen((o) => !o)} aria-expanded={cardFiltersOpen} style={{
            ...cardToolBtn,
            borderColor: hasColFilters || sort ? "var(--brand)" : "var(--line)",
            color: hasColFilters || sort ? "var(--brand)" : "var(--ink-soft)",
        }}>
          Filters &amp; sort{hasColFilters || sort ? " •" : ""}
          <span aria-hidden="true" style={{ transform: cardFiltersOpen ? "rotate(180deg)" : "none" }}>▾</span>
        </button>

        {cardFiltersOpen && (<div style={{ background: "var(--surface)", border: "1px solid var(--line-soft)", borderRadius: 10, padding: 12, display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8 }}>
            <label style={cardFilterLabel}>Case
              <input placeholder="Case code" value={colFilters.caseCode} onChange={(e) => setCol("caseCode", e.target.value)} style={cardFilterInput}/>
            </label>
            <label style={cardFilterLabel}>Customer
              <input placeholder="Customer" value={colFilters.customer} onChange={(e) => setCol("customer", e.target.value)} style={cardFilterInput}/>
            </label>
            <label style={cardFilterLabel}>From
              <input type="date" value={colFilters.dateFrom} onChange={(e) => setCol("dateFrom", e.target.value)} style={cardFilterInput}/>
            </label>
            <label style={cardFilterLabel}>To
              <input type="date" value={colFilters.dateTo} onChange={(e) => setCol("dateTo", e.target.value)} style={cardFilterInput}/>
            </label>
            <label style={cardFilterLabel}>Type
              <select value={colFilters.type} onChange={(e) => setCol("type", e.target.value)} style={cardFilterInput}>
                <option value="">All</option>
                {typeOptions.map((t) => (<option key={t} value={t}>{t}</option>))}
              </select>
            </label>
            <label style={cardFilterLabel}>Stage
              <select value={colFilters.stage} onChange={(e) => setCol("stage", e.target.value)} style={cardFilterInput}>
                <option value="">All</option>
                {stageOptions.map((s) => (<option key={s} value={s}>{s.replace(/_/g, " ")}</option>))}
              </select>
            </label>
            <label style={cardFilterLabel}>Legal firm
              <select value={colFilters.legalFirmId} onChange={(e) => setCol("legalFirmId", e.target.value)} style={cardFilterInput}>
                <option value="">All</option>
                {legalFirmOptions.map((f) => (<option key={f.id} value={f.id}>{f.name}</option>))}
              </select>
            </label>
            <label style={cardFilterLabel}>Priority
              <select value={colFilters.priority} onChange={(e) => setCol("priority", e.target.value)} style={cardFilterInput}>
                <option value="">All</option>
                {priorityOptions.map((p) => (<option key={p} value={p}>{p}</option>))}
              </select>
            </label>
            <label style={{ ...cardFilterLabel, gridColumn: "1 / -1" }}>Sort by
              <select value={sort ? `${sort.key}:${sort.dir}` : ""} onChange={(e) => {
                const [key, dir] = e.target.value.split(":");
                setSort(key ? { key, dir } : null);
            }} style={cardFilterInput}>
                <option value="">Default order</option>
                {SORT_OPTIONS.map(([key, label]) => [
                    <option key={`${key}:asc`} value={`${key}:asc`}>{label} ↑</option>,
                    <option key={`${key}:desc`} value={`${key}:desc`}>{label} ↓</option>,
                ])}
              </select>
            </label>
          </div>)}

        {sortedCases.map((c) => (<Link key={c.id} to={`/legal-cases/cases/${c.id}`} style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
                padding: "12px 14px",
                background: "var(--surface)",
                border: "1px solid var(--line-soft)",
                borderRadius: 10,
                textDecoration: "none",
                color: "inherit",
            }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
              <span className="mono" style={{ fontSize: "0.82rem", color: "var(--accent-ink)", fontWeight: 700, overflowWrap: "anywhere" }}>{c.caseCode}</span>
              {c.priority && <span style={{ ...chip, ...(PRIORITY_CHIP[c.priority] ?? {}) }}>{c.priority}</span>}
            </div>
            <div style={{ fontSize: "0.92rem", fontWeight: 600, color: "var(--ink)", overflowWrap: "anywhere" }}>{customerName(c.customerId)}</div>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, fontSize: "0.76rem", color: "var(--ink-soft)" }}>
              <span style={{ ...chip, background: "var(--st-info-bg)", color: "var(--st-info)" }}>{c.legalStage.replace(/_/g, " ")}</span>
              <span>{c.caseType}</span>
            </div>
            {c.legalFirmId && (<div style={{ fontSize: "0.76rem", color: "var(--ink-faint)", overflowWrap: "anywhere" }}>
                Legal firm: {legalFirmName(c.legalFirmId)}
              </div>)}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, marginTop: 2 }}>
              <span className="mono" style={{ fontSize: "0.76rem", color: "var(--ink-faint)" }}>
                {c.caseDate ? new Date(c.caseDate).toLocaleDateString() : "—"}
              </span>
              <span className="mono tabnum" style={{ fontSize: "0.86rem", fontWeight: 700, color: "var(--ink)" }}>
                {formatQAR(c.financialSummary.outstandingBalance)}
              </span>
            </div>
          </Link>))}
        {sortedCases.length === 0 && !error && (<div style={{ padding: 24, textAlign: "center", color: "var(--ink-faint)", fontSize: "0.85rem", background: "var(--surface)", border: "1px solid var(--line-soft)", borderRadius: 10 }}>
            {search.trim() || hasColFilters ? "No cases match your filters." : "No cases found."}
          </div>)}
      </div>
    </div>);
}
const SORT_OPTIONS = [
    ["caseDate", "Case date"],
    ["outstanding", "Outstanding"],
    ["priority", "Priority"],
    ["caseCode", "Case code"],
    ["customer", "Customer"],
    ["legalStage", "Stage"],
];
const chip = {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 999,
    fontSize: "0.68rem",
    fontWeight: 700,
    letterSpacing: "0.02em",
    whiteSpace: "nowrap",
};
const PRIORITY_CHIP = {
    CRITICAL: { background: "var(--st-risk-bg)", color: "var(--st-risk)" },
    HIGH: { background: "var(--st-risk-bg)", color: "var(--st-risk)" },
    MEDIUM: { background: "var(--st-pending-bg)", color: "var(--st-pending)" },
    LOW: { background: "var(--surface-2)", color: "var(--ink-soft)" },
};
const cardToolBtn = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    alignSelf: "flex-start",
    border: "1px solid var(--line)",
    background: "var(--surface)",
    borderRadius: 8,
    padding: "8px 12px",
    fontSize: "0.82rem",
    fontWeight: 600,
    fontFamily: "inherit",
    cursor: "pointer",
};
const cardFilterLabel = {
    display: "flex",
    flexDirection: "column",
    gap: 3,
    minWidth: 0,
    fontSize: "0.66rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: "var(--ink-faint)",
};
const cardFilterInput = {
    ...colFilterInput,
    padding: "7px 8px",
    fontSize: "0.82rem",
    textTransform: "none",
    letterSpacing: "normal",
    fontWeight: 400,
    color: "var(--ink)",
};
